import {
  PlanningScope,
  PlanningType as ApiPlanningType,
  type Building,
  type Planning,
  type PutPlanningBody,
} from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { hourEndToMinutes, toMinutes, type CopyToDaysSchemaType } from "~/features/planning";
import type { EnhancedRoom } from "~/types/room.type";
import type { EnhancedValve } from "~/types/valve.type";

export const getCurrentTimeslot = (plannings: Planning[]): Planning => {
  const today = dayjs();
  const currentDay = today.locale("en").format("dddd").toLowerCase();
  const currentTime = today.hour() * 60 + today.minute();

  const timeslots = plannings.filter(
    p => p.day === currentDay && toMinutes(p.hourStart) <= currentTime && hourEndToMinutes(p.hourEnd) > currentTime,
  );

  const current = timeslots.find(t => t.userDefined) ?? timeslots[0];
  if (!current) {
    throw new Error("No timeslot found for current day");
  }
  return current;
};

export type PlanningType = ApiPlanningType | "frostFree";

export const getTemperature = (building: Building, room: EnhancedRoom, type: PlanningType | "frostFree") => {
  switch (type) {
    case "comfort":
      return room.comfortTemperature;
    case "night":
      return room.nightTemperature;
    case "absence":
      return building.absenceTemperature;
    case "preComfort":
      return building.preComfortTemperature;
    case "frostFree":
      return building.frostFreeTemperature;
    default:
      return 0;
  }
};

/**
 * Retrieves the current planning type based on the room data.
 * @returns The current planning type.
 */
export const getCurrentPlanningType = (
  autoConf: boolean,
  anticipation: boolean,
  autoPreConfEnabled: boolean,
  isFrostFree: boolean,
  currentTimeSlot: PlanningType,
  connectedRoomValves: EnhancedValve[],
  building: Building,
  room: EnhancedRoom,
): PlanningType => {
  switch (true) {
    case isFrostFree:
      return "frostFree";
    case autoConf:
      return ApiPlanningType.COMFORT;
    case anticipation: {
      const firstValveInAnticipation = connectedRoomValves.find(
        v => v.stateData?.anticipation && v.correctedTemp !== undefined && v.correction !== undefined,
      );
      if (firstValveInAnticipation) {
        const { correctedTemp, correction } = firstValveInAnticipation;
        if (correctedTemp === undefined || correction === undefined) {
          return currentTimeSlot;
        }
        const consignTempInAnticipation = correctedTemp - correction;
        const typePlanning: PlanningType[] = [ApiPlanningType.COMFORT, ApiPlanningType.PRE_COMFORT];
        typePlanning.forEach(type => {
          if (consignTempInAnticipation === getTemperature(building, room, type)) return type;
        });
        return currentTimeSlot;
      } else {
        return currentTimeSlot;
      }
    }
    case autoPreConfEnabled:
      return ApiPlanningType.PRE_COMFORT;
    default:
      return currentTimeSlot;
  }
};

export const copyToDaysSchemaTypeToPutPlanningBody = (
  roomId: string,
  data: CopyToDaysSchemaType,
  userDefinedPlannings: Planning[],
): PutPlanningBody => {
  if (userDefinedPlannings.some(planning => !planning.userDefined)) {
    throw new Error("Some plannings are not user defined" + JSON.stringify(userDefinedPlannings));
  }
  const { dayToCopy, targetDays } = data;
  const copiedPlannings = userDefinedPlannings.filter(planning => planning.day === dayToCopy);
  const newPlannings = targetDays
    .filter(day => day !== dayToCopy)
    .flatMap(day => copiedPlannings.map(planning => ({ ...planning, day })));
  const otherPlannings = userDefinedPlannings.filter(
    planning => planning.day !== dayToCopy && !targetDays.includes(planning.day),
  );
  return {
    scope: PlanningScope.ROOM,
    parentIds: [roomId],
    plannings: [...copiedPlannings, ...newPlannings, ...otherPlannings],
  };
};
